import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { NovedadesApi } from "../../../api/Novedades/NovedadesApi";
import {
  dashCountChipSx,
  dashListRowSx,
  dashLinkBtnSx,
  dashIconWellSx,
  getDashAccent,
} from "../myDashboardStyles";

export default function NovedadesWidget({ editing }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const accent = getDashAccent("widget-novedades", "widgets", theme.palette.mode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let data = await NovedadesApi.getNuevas();
        if (!Array.isArray(data) || data.length === 0) {
          data = await NovedadesApi.getAll();
        }
        if (!cancelled) {
          setItems(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.75 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box
          sx={(theme) => ({
            ...dashIconWellSx(theme, accent),
            width: 38,
            height: 38,
            borderRadius: "12px",
          })}
        >
          <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={800} letterSpacing={0.1}>
          {t("myDashboard.widgetNovedades")}
        </Typography>
        <Box sx={(theme) => dashCountChipSx(theme, accent)}>{items.length}</Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            {t("myDashboard.noNews")}
          </Typography>
        ) : (
          <List dense disablePadding>
            {items.map((item) => (
              <ListItem
                key={item.Id || item.id}
                disableGutters
                sx={dashListRowSx}
              >
                <ListItemText
                  primary={
                    item.Titulo || item.titulo || item.Nombre || `#${item.Id}`
                  }
                  secondary={item.Fecha || item.fecha || item.CreatedAt || null}
                  primaryTypographyProps={{
                    variant: "body2",
                    noWrap: true,
                    fontWeight: 600,
                  }}
                  secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {!editing && (
        <Button
          size="small"
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/novedadesInsertar")}
          sx={dashLinkBtnSx}
        >
          {t("myDashboard.openNews")}
        </Button>
      )}
    </Box>
  );
}
